import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/Reservation.css';
import gite1 from '../assets/image/giteTiwizi.png';
import gite2 from '../assets/image/entree-gite.png';
import gite3 from '../assets/image/chambre-gite.png';
import gite4 from '../assets/image/salon-gite.png';
import mountainImg from '../assets/image/mountain_village.png';

/* ─── Pricing data ─────────────────────────────────────────── */
const PRICING_ROWS = (t) => [
  {
    category: t('reservation.row1Cat'),
    formula:  t('reservation.row1Form'),
    lodging:  '75 DH',
    food:     '125 DH',
    activity: '50 DH',
  },
  {
    category: t('reservation.row2Cat'),
    formula:  t('reservation.row2Form'),
    lodging:  '75 DH',
    food:     '150 DH',
    activity: '50 DH',
  },
  {
    category: t('reservation.row3Cat'),
    formula:  t('reservation.row3Form'),
    lodging:  '100 DH',
    food:     '150 DH',
    activity: '50 DH',
  },
];

/* ─── Initial form state ───────────────────────────────────── */
const INITIAL_FORM = {
  name:          '',
  email:         '',
  phone:         '',
  guests:        '',
  arrivalDate:   '',
  departureDate: '',
  message:       '',
};

/* ─── Component ────────────────────────────────────────────── */
const Reservation = () => {
  const { t } = useTranslation();
  const [formData,    setFormData]    = useState(INITIAL_FORM);
  const [errors,      setErrors]      = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!formData.name.trim())  newErrors.name  = t('reservation.errName');
    if (!formData.email.trim()) newErrors.email = t('reservation.errEmail');
    if (!formData.phone.trim()) newErrors.phone = t('reservation.errPhone');

    if (!formData.guests) {
      newErrors.guests = t('reservation.errGuests');
    } else if (parseInt(formData.guests, 10) < 1) {
      newErrors.guests = t('reservation.errGuestsMin');
    }

    if (!formData.arrivalDate) {
      newErrors.arrivalDate = t('reservation.errArrival');
    } else if (new Date(formData.arrivalDate) < today) {
      newErrors.arrivalDate = t('reservation.errArrivalPast');
    }

    if (!formData.departureDate) {
      newErrors.departureDate = t('reservation.errDeparture');
    } else if (formData.arrivalDate && new Date(formData.departureDate) < new Date(formData.arrivalDate)) {
      newErrors.departureDate = t('reservation.errDepartureBeforeArrival');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/reservations/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            nom: formData.name,
            email: formData.email,
            telephone: formData.phone,
            nb_personnes: parseInt(formData.guests, 10),
            date_arrivee: formData.arrivalDate,
            date_depart: formData.departureDate,
            message: formData.message
          }),
        });
        
        if (response.ok) {
          setIsSubmitted(true);
        } else {
          console.error("Erreur lors de la réservation");
          alert(t('reservation.alertError'));
        }
      } catch (error) {
        console.error("Erreur de connexion:", error);
        alert(t('reservation.alertConn'));
      }
    }
  };

  const handleReset = () => {
    setFormData(INITIAL_FORM);
    setErrors({});
    setIsSubmitted(false);
  };

  /* ── helpers ── */
  const fieldClass = (key) =>
    `res-field__input${errors[key] ? ' res-field__input--error' : ''}`;

  return (
    <div className="res-page">

      {/* ════════════════════════════════════════
          HERO
      ════════════════════════════════════════ */}
      <section className="ap-hero" style={{ backgroundImage: `url(${mountainImg})` }}>
        <div className="ap-hero-overlay" />
        <div className="ap-hero-content">
          <span className="ap-hero-badge">{t('reservation.badgeHero')}</span>
          <h1 className="ap-hero-title">
            {t('reservation.titleHero1')} <span className="keyword">{t('reservation.titleKeyword')}</span>
          </h1>
          <p className="ap-hero-sub">
            {t('reservation.subHero')}
          </p>
        </div>
        <div className="ap-hero-wave">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path fill="var(--background-solid)" d="M0,100 L0,40 C360,100 1080,0 1440,60 L1440,100 Z" />
          </svg>
        </div>
      </section>

      {/* ════════════════════════════════════════
          DESCRIPTION
      ════════════════════════════════════════ */}
      <section className="res-about" aria-label="À propos du gîte">
        <div className="res-about__inner">
          <h2 className="res-about__title">{t('reservation.aboutTitle')}</h2>
          <p className="res-about__text">
            {t('reservation.aboutText')}
          </p>
        </div>
      </section>

      {/* ════════════════════════════════════════
          PHOTO GALLERY
      ════════════════════════════════════════ */}
      <section className="res-gallery" aria-label="Galerie photos du gîte">
        <div className="res-gallery__grid">
          <div className="res-gallery__item">
            <img src={gite1} alt="Gîte Tiwizi — vue extérieure" className="res-gallery__img" />
          </div>
          <div className="res-gallery__item">
            <img src={gite2} alt="Gîte Tiwizi — entrée" className="res-gallery__img" />
          </div>
          <div className="res-gallery__item">
            <img src={gite3} alt="Gîte Tiwizi — chambre" className="res-gallery__img" />
          </div>
          <div className="res-gallery__item">
            <img src={gite4} alt="Gîte Tiwizi — salon" className="res-gallery__img" />
          </div>
        </div>
        <p className="res-gallery__caption">{t('reservation.galleryCaption')}</p>
      </section>

      {/* ════════════════════════════════════════
          FLOATING CARD — TARIFS
      ════════════════════════════════════════ */}
      <div className="res-card-wrapper">
        <div className="res-card res-slide-up" id="reservation-tarifs">

          {isSubmitted ? (

            /* ── Success ── */
            <div className="res-success">
              <div className="res-success__icon" aria-hidden="true">
                <svg viewBox="0 0 52 52">
                  <circle className="res-success__circle" cx="26" cy="26" r="25" fill="none" />
                  <path  className="res-success__check"  fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                </svg>
              </div>
              <h2>{t('reservation.successTitle')}</h2>
              <p>
                {t('reservation.successText')}
              </p>
              <button className="res-submit res-submit--outline" onClick={handleReset}>
                {t('reservation.btnNew')}
              </button>
            </div>

          ) : (
            <>
              {/* ── Section title ── */}
              <div className="res-section-header">
                <h2 className="res-section-header__title">{t('reservation.pricingTitle')}</h2>
                <p className="res-section-header__sub">{t('reservation.pricingSub')}</p>
              </div>

              {/* ── Pricing table ── */}
              <div className="res-pricing">
                <div className="res-pricing__scroll" role="region" aria-label="Tableau des tarifs">
                  <table className="res-pricing__table">
                    <thead>
                      <tr>
                        <th scope="col">{t('reservation.thCat')}</th>
                        <th scope="col">{t('reservation.thForm')}</th>
                        <th scope="col">{t('reservation.thLodging')}<br /><span className="res-th-sub">{t('reservation.thSub')}</span></th>
                        <th scope="col">{t('reservation.thFood')}<br /><span className="res-th-sub">{t('reservation.thSub')}</span></th>
                        <th scope="col">{t('reservation.thAct')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {PRICING_ROWS(t).map((row, i) => (
                        <tr key={i}>
                          <td className="res-td--bold">{row.category}</td>
                          <td>
                            <span className="res-formula-badge">{row.formula}</span>
                          </td>
                          <td className="res-td--price">{row.lodging}</td>
                          <td className="res-td--price">{row.food}</td>
                          <td className="res-td--price">{row.activity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ════════════════════════════════════════
          CARTE FORMULAIRE SÉPARÉE
      ════════════════════════════════════════ */}
      {!isSubmitted && (
        <div className="res-form-card-wrapper">
          <div className="res-form-card" id="reservation-form">
            <div className="res-form-card__header">
              <h2 className="res-form-card__title">
                {t('reservation.formTitle1')}<span className="keyword">{t('reservation.formTitleKeyword')}</span>
              </h2>
              <p className="res-form-card__sub">{t('reservation.formSub')}</p>
            </div>

            <form
              className="res-form"
              onSubmit={handleSubmit}
              noValidate
              aria-label="Formulaire de réservation"
            >
                {/* Row 1 */}
                <div className="res-form__row">
                  <div className="res-field">
                    <label className="res-field__label" htmlFor="res-name">
                      {t('reservation.labelName')} <span className="res-required" aria-hidden="true">*</span>
                    </label>
                    <input
                      id="res-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t('reservation.phName')}
                      className={fieldClass('name')}
                      aria-describedby={errors.name ? 'err-name' : undefined}
                      aria-invalid={!!errors.name}
                      autoComplete="name"
                    />
                    {errors.name && (
                      <span id="err-name" className="res-field__error" role="alert">{errors.name}</span>
                    )}
                  </div>

                  <div className="res-field">
                    <label className="res-field__label" htmlFor="res-email">
                      {t('reservation.labelEmail')} <span className="res-required" aria-hidden="true">*</span>
                    </label>
                    <input
                      id="res-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('reservation.phEmail')}
                      className={fieldClass('email')}
                      aria-describedby={errors.email ? 'err-email' : undefined}
                      aria-invalid={!!errors.email}
                      autoComplete="email"
                    />
                    {errors.email && (
                      <span id="err-email" className="res-field__error" role="alert">{errors.email}</span>
                    )}
                  </div>
                </div>

                {/* Row 2 */}
                <div className="res-form__row">
                  <div className="res-field">
                    <label className="res-field__label" htmlFor="res-phone">
                      {t('reservation.labelPhone')} <span className="res-required" aria-hidden="true">*</span>
                    </label>
                    <input
                      id="res-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t('reservation.phPhone')}
                      className={fieldClass('phone')}
                      aria-describedby={errors.phone ? 'err-phone' : undefined}
                      aria-invalid={!!errors.phone}
                      autoComplete="tel"
                    />
                    {errors.phone && (
                      <span id="err-phone" className="res-field__error" role="alert">{errors.phone}</span>
                    )}
                  </div>

                  <div className="res-field">
                    <label className="res-field__label" htmlFor="res-guests">
                      {t('reservation.labelGuests')} <span className="res-required" aria-hidden="true">*</span>
                    </label>
                    <input
                      id="res-guests"
                      type="number"
                      name="guests"
                      min="1"
                      max="100"
                      value={formData.guests}
                      onChange={handleChange}
                      placeholder={t('reservation.phGuests')}
                      className={fieldClass('guests')}
                      aria-describedby={errors.guests ? 'err-guests' : undefined}
                      aria-invalid={!!errors.guests}
                    />
                    {errors.guests && (
                      <span id="err-guests" className="res-field__error" role="alert">{errors.guests}</span>
                    )}
                  </div>
                </div>

                {/* Row 3 */}
                <div className="res-form__row">
                  <div className="res-field">
                    <label className="res-field__label" htmlFor="res-arrival">
                      {t('reservation.labelArrival')} <span className="res-required" aria-hidden="true">*</span>
                    </label>
                    <input
                      id="res-arrival"
                      type="date"
                      name="arrivalDate"
                      value={formData.arrivalDate}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      className={fieldClass('arrivalDate')}
                      aria-describedby={errors.arrivalDate ? 'err-arrival' : undefined}
                      aria-invalid={!!errors.arrivalDate}
                    />
                    {errors.arrivalDate && (
                      <span id="err-arrival" className="res-field__error" role="alert">{errors.arrivalDate}</span>
                    )}
                  </div>

                  <div className="res-field">
                    <label className="res-field__label" htmlFor="res-departure">
                      {t('reservation.labelDeparture')} <span className="res-required" aria-hidden="true">*</span>
                    </label>
                    <input
                      id="res-departure"
                      type="date"
                      name="departureDate"
                      value={formData.departureDate}
                      onChange={handleChange}
                      min={formData.arrivalDate || new Date().toISOString().split('T')[0]}
                      className={fieldClass('departureDate')}
                      aria-describedby={errors.departureDate ? 'err-departure' : undefined}
                      aria-invalid={!!errors.departureDate}
                    />
                    {errors.departureDate && (
                      <span id="err-departure" className="res-field__error" role="alert">{errors.departureDate}</span>
                    )}
                  </div>
                </div>

                {/* Message — full width */}
                <div className="res-field">
                  <label className="res-field__label" htmlFor="res-message">
                    {t('reservation.labelMsg')}
                  </label>
                  <textarea
                    id="res-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    placeholder={t('reservation.phMsg')}
                    className="res-field__input res-field__input--textarea"
                  />
                </div>

                <div style={{textAlign: 'center', marginTop: '10px'}}>
                  <button type="submit" className="res-submit">
                    {t('reservation.btnSubmit')}
                  </button>
                </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reservation;
